import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { AsyncStatus } from '../types';

/**
 * Registration page — create a new account via Supabase Auth.
 * After sign-up Supabase sends a confirmation email by default;
 * we show a success state prompting the user to check their inbox.
 */
function RegisterPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    // Client-side validation
    if (password.length < 8) {
      setErrorMsg('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setStatus('loading');

    const { error } = await supabase.auth.signUp({ email, password });

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
    } else {
      setStatus('success');
    }
  };

  // ── Success state ──────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <main className="page-container flex flex-col items-center justify-center min-h-screen animate-fade-in">
        <div className="mb-6 w-16 h-16 rounded-2xl bg-accent-500 flex items-center justify-center shadow-lg">
          <span className="text-3xl" role="img" aria-label="email sent">✉️</span>
        </div>
        <h1 className="font-display text-3xl font-bold text-white mb-2">Check your inbox!</h1>
        <p className="text-white/60 text-sm text-center max-w-xs mb-8">
          We've sent a confirmation link to <span className="text-brand-300 font-medium">{email}</span>.
          Click it to activate your account.
        </p>
        <button
          id="go-to-login-after-register"
          onClick={() => navigate('/login', { replace: true })}
          className="btn-secondary"
        >
          Back to Login
        </button>
      </main>
    );
  }

  // ── Form state ─────────────────────────────────────────────────────────────
  return (
    <main className="page-container flex flex-col items-center justify-center min-h-screen animate-fade-in">
      {/* Logo */}
      <div className="mb-6 w-16 h-16 rounded-2xl bg-brand-500 flex items-center justify-center shadow-lg">
        <span className="text-3xl" role="img" aria-label="paw">🐾</span>
      </div>

      <h1 className="font-display text-3xl font-bold text-white mb-1">Create account</h1>
      <p className="text-white/50 text-sm mb-8">Your pet's health passport starts here</p>

      {/* Card */}
      <div className="glass-card w-full max-w-sm p-6 animate-slide-up">
        <form id="register-form" onSubmit={handleSubmit} noValidate className="space-y-4">

          {/* Email */}
          <div>
            <label htmlFor="register-email" className="block text-sm font-medium text-white/80 mb-1">
              Email
            </label>
            <input
              id="register-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input-field"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="register-password" className="block text-sm font-medium text-white/80 mb-1">
              Password
              <span className="ml-1 text-white/30 font-normal">(min. 8 chars)</span>
            </label>
            <input
              id="register-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input-field"
            />
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="register-confirm-password" className="block text-sm font-medium text-white/80 mb-1">
              Confirm Password
            </label>
            <input
              id="register-confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="input-field"
            />
          </div>

          {/* Error message */}
          {(status === 'error' || errorMsg) && (
            <div
              role="alert"
              className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3"
            >
              {errorMsg}
            </div>
          )}

          {/* Submit */}
          <button
            id="register-submit"
            type="submit"
            disabled={status === 'loading'}
            className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'loading' ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                Creating account…
              </>
            ) : (
              'Create account'
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-white/30 text-xs">OR</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <p className="text-center text-sm text-white/50">
          Already have an account?{' '}
          <Link
            to="/login"
            id="go-to-login"
            className="text-brand-400 font-semibold hover:text-brand-300 transition-colors"
          >
            Sign in
          </Link>
        </p>
      </div>

      {/* Privacy note */}
      <p className="mt-6 text-xs text-white/25 text-center max-w-xs">
        By creating an account you agree to our terms. Your data is stored securely in India.
      </p>
    </main>
  );
}

export default RegisterPage;
